import { useTranslation } from 'next-i18next'
import s from '@components/MultiBuyProducts/MultiBuyProducts.module.scss'
import {
    ProductInterface,
    SimpleProduct,
    ConfigurableProduct
} from '@pylot-data/fwrdschema'
import { MultiBuyProductCard } from './MultiBuyProductCard'
import React, { useState, useEffect } from 'react'
import { formatPrice } from '@corratech/pylot-price/src/utils/formatPrice'
import { useRouter } from 'next/router'
import { Button } from '@corratech/pylot-ui'
import cn from 'classnames'
import formStyles from '@commonStyles/Forms.module.scss'
import { ProductStockStatus } from '@pylot-data/enums/ProductStockStatus.d'
import { useIsMobile } from '@components/common/IsMobile/useIsMobile'

type MultiBuyProductsProps = {
    product: SimpleProduct | ConfigurableProduct
    title: string
    addProductsToCart: (multiBuySkus: string[]) => void
    isAdding: boolean
    isButtonDisabled: boolean
    mainButtonLabel: string
    variant: SimpleProduct
}

export const MultiBuyProducts = ({
    product,
    title,
    addProductsToCart,
    isAdding,
    isButtonDisabled,
    mainButtonLabel,
    variant
}: MultiBuyProductsProps) => {
    const { t } = useTranslation('common')
    const { locale = 'en' } = useRouter()
    const isMobile = useIsMobile()
    const relatedProduct = product?.related_products?.filter(
        (rProducts) => rProducts?.stock_status === ProductStockStatus.InStock
    )
    // updating price/thumbnail for main product with variant
    const updatedProduct = {
        ...product,
        price_range: variant.price_range,
        thumbnail: variant.thumbnail
    }

    const {
        price_range: {
            minimum_price: {
                final_price: { currency: mainCurrency }
            }
        }
    } = updatedProduct

    const initialSelectedProductSkus = [
        updatedProduct?.sku,
        ...(relatedProduct?.map((product) => product?.sku) || [])
    ]
    const [selectedProduct, setSelectedProduct] = useState(
        initialSelectedProductSkus
    )

    useEffect(() => {
        setSelectedProduct(initialSelectedProductSkus)
    }, [product])

    if (
        !relatedProduct?.length ||
        !title ||
        updatedProduct.stock_status === ProductStockStatus.OutOfStock
    )
        return null

    const getProductPrice = () => {
        let totalPrice = relatedProduct.reduce((acc, _product) => {
            if (selectedProduct.includes(_product?.sku)) {
                return (
                    acc +
                    (_product?.price_range?.minimum_price?.final_price?.value ||
                        0)
                )
            }
            return acc
        }, 0)

        if (selectedProduct.includes(updatedProduct?.sku)) {
            totalPrice =
                totalPrice +
                (updatedProduct?.price_range?.minimum_price?.final_price
                    ?.value || 0)
        }

        return formatPrice(
            { value: totalPrice, currency: mainCurrency },
            locale
        )
    }

    const addProductToSelected = (productSelect: ProductInterface) => {
        const sku = productSelect?.sku
        if (selectedProduct.includes(sku)) {
            setSelectedProduct([...selectedProduct.filter((e) => e !== sku)])
        } else {
            selectedProduct.push(sku)
            setSelectedProduct([...selectedProduct])
        }
    }

    const addAllproductsToCart = async () => {
        addProductsToCart(selectedProduct as string[])
    }
    const processedIsDisabled =
        !!isAdding ||
        (isButtonDisabled && selectedProduct.includes(updatedProduct?.sku))
    const getButtonLabel = () => {
        if (isAdding) return t('Adding...')
        if (processedIsDisabled) return mainButtonLabel
        return t('Add to Bag')
    }

    return (
        <div className={s['related-products-wrapper']}>
            <div className={cn(s['related-products'], formStyles.root)}>
                <div className={s['heading-container']}>
                    <h3 className={s['heading-title']}>{title}</h3>
                </div>
                <div
                    className={
                        s[isMobile ? 'products-list-mobile' : 'products-list']
                    }
                >
                    <MultiBuyProductCard
                        product={product as ProductInterface}
                        selected={false}
                        isMobile={false}
                        onClick={() => null}
                        isPdpProduct
                    />
                    {relatedProduct.map((rproduct, index: number) => {
                        return (
                            <MultiBuyProductCard
                                key={index}
                                onClick={() =>
                                    addProductToSelected(
                                        rproduct as ProductInterface
                                    )
                                }
                                product={rproduct as ProductInterface}
                                selected={selectedProduct.includes(
                                    rproduct?.sku as never
                                )}
                                isMobile={isMobile}
                            />
                        )
                    })}
                    {!!selectedProduct.length && (
                        <div className={s['action-container']}>
                            <p className={s['buy-all-product']}>
                                {selectedProduct.length > 1
                                    ? t(`Buy all {{items}}`, {
                                          items: selectedProduct.length
                                      })
                                    : t(`Buy 1 item`)}
                            </p>
                            <p className={s['total-price']}>
                                {t(`Total: {{price}}`, {
                                    price: getProductPrice()
                                })}
                            </p>
                            <Button
                                onClick={() => addAllproductsToCart()}
                                variant="primary"
                                disabled={processedIsDisabled}
                                className={cn(
                                    s['multibuy-add-to-cart'],
                                    'btn-sm'
                                )}
                            >
                                {getButtonLabel()}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default MultiBuyProducts
